import {
  IDLE_RPM,
  MAX_RPM,
  STATIC_THRUST,
  THRUST_ZERO_SPEED,
  FUEL_BURN_MAX,
} from './constants';

/**
 * Simple fixed-pitch prop + piston engine model for the C172S.
 *
 * RPM follows a commanded value (from throttle) with a short lag, is scaled by
 * mixture richness and reduced at altitude. Thrust falls off with airspeed
 * (fixed-pitch prop) and with air density. This is not a manifold-pressure
 * model, but it produces plausible tach readings, power and thrust for training.
 */

export interface EngineOutput {
  rpm: number;
  thrust: number; // N along body +x
  fuelBurn: number; // litres/sec
  powerFraction: number; // 0..1 of max
}

export class Engine {
  running = true;

  update(
    rpm: number,
    throttle: number,
    mixture: number,
    densityRatio: number,
    trueAirspeed: number,
    dt: number,
  ): EngineOutput {
    if (!this.running) {
      // Windmilling / spool down toward zero
      const target = 0;
      const next = rpm + (target - rpm) * Math.min(1, dt * 1.5);
      return { rpm: next, thrust: 0, fuelBurn: 0, powerFraction: 0 };
    }

    // Mixture effectiveness peaks near ~0.85 (best power) and drops if too lean.
    const mixtureFactor = Math.max(0.05, 1 - Math.abs(mixture - 0.85) * 1.6);

    // Commanded RPM from throttle, reduced slightly by altitude (thinner air).
    const altFactor = 0.75 + 0.25 * densityRatio;
    const commanded =
      (IDLE_RPM + throttle * (MAX_RPM - IDLE_RPM)) * mixtureFactor * altFactor;

    // First-order lag toward commanded RPM (engine + prop inertia).
    const nextRpm = rpm + (commanded - rpm) * Math.min(1, dt * 2.2);

    const rpmFraction = Math.max(0, (nextRpm - IDLE_RPM) / (MAX_RPM - IDLE_RPM));
    const powerFraction = rpmFraction * mixtureFactor * densityRatio;

    // Fixed-pitch prop thrust: static thrust decaying to zero near cruise TAS.
    const speedFalloff = Math.max(0, 1 - trueAirspeed / THRUST_ZERO_SPEED);
    const thrust =
      STATIC_THRUST * rpmFraction * densityRatio * (0.35 + 0.65 * speedFalloff);

    const fuelBurn = FUEL_BURN_MAX * (0.15 + 0.85 * rpmFraction) * mixtureFactor;

    return { rpm: nextRpm, thrust, fuelBurn, powerFraction };
  }
}
